import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { trackEvent } from '../hooks/useAnalytics';

const AuthContext = createContext(null);

// Safe localStorage wrapper for mobile compatibility
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage getItem error:', e);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage setItem error:', e);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('localStorage removeItem error:', e);
    }
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authCheckRef = useRef(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    // Prevent duplicate calls
    if (authCheckRef.current) return;
    authCheckRef.current = true;
    
    try {
      // Get session token from localStorage
      const storedToken = safeStorage.getItem('session_token');
      
      // If no token, user is not authenticated
      if (!storedToken) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        authCheckRef.current = false;
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // Token invalid or expired
        safeStorage.removeItem('session_token');
        safeStorage.removeItem('auth_timestamp');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // On network error, don't clear token - might just be offline
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
      authCheckRef.current = false;
    }
  }, [backendUrl]);

  useEffect(() => {
    // CRITICAL (Emergent Auth playbook): If returning from OAuth callback,
    // skip /auth/me check. AuthCallback will exchange the session_id and
    // establish the session first. Without this, checkAuth races against
    // AuthCallback and can cause "unstable" Google logins.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setIsLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);
  
  // Listen for storage changes (e.g., logout in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'session_token') {
        if (!e.newValue) {
          setUser(null);
          setIsAuthenticated(false);
        } else {
          checkAuth();
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  // Google Login
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Email Register
  const register = async (email, password, name, phone) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Kayıt işlemi başarısız');
      }

      // Store session token in localStorage FIRST
      if (data.session_token) {
        const saved = safeStorage.setItem('session_token', data.session_token);
        safeStorage.setItem('auth_timestamp', Date.now().toString());
        
        if (!saved) {
          console.warn('Failed to save session token to localStorage');
        }
      }

      // Then update state
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Track registration event
      trackEvent({
        event_type: 'user_register',
        metadata: { auth_type: 'email' }
      });
      
      return { success: true, user: data.user, session_token: data.session_token };
    } catch (error) {
      console.error('Register error:', error);
      return { success: false, error: error.message || 'Bağlantı hatası' };
    }
  };

  // Email Login
  const loginWithEmail = async (email, password) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Giriş başarısız');
      }

      // Store session token in localStorage FIRST
      if (data.session_token) {
        const saved = safeStorage.setItem('session_token', data.session_token);
        safeStorage.setItem('auth_timestamp', Date.now().toString());
        
        if (!saved) {
          console.warn('Failed to save session token to localStorage');
        }
      }

      // Then update state
      setUser(data.user);
      setIsAuthenticated(true);
      
      // Track login event
      trackEvent({
        event_type: 'user_login',
        metadata: { auth_type: 'email' }
      });
      
      return { success: true, user: data.user, session_token: data.session_token };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Bağlantı hatası' };
    }
  };

  const processSession = async (sessionId) => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store session token in localStorage FIRST
        if (data.session_token) {
          safeStorage.setItem('session_token', data.session_token);
          safeStorage.setItem('auth_timestamp', Date.now().toString());
        }
        
        // Then update state
        setUser(data.user);
        setIsAuthenticated(true);
        
        // Track Google login event
        trackEvent({
          event_type: 'user_login',
          metadata: { auth_type: 'google' }
        });
        
        return data.user;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Session processing failed');
      }
    } catch (error) {
      console.error('Session processing error:', error);
      // Clear any stale auth data
      safeStorage.removeItem('session_token');
      safeStorage.removeItem('auth_timestamp');
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  const logout = async () => {
    // Track logout event
    trackEvent({
      event_type: 'user_logout',
    });
    
    // Get token for logout request
    const token = safeStorage.getItem('session_token');
    
    try {
      if (token) {
        await fetch(`${backendUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      safeStorage.removeItem('session_token');
      safeStorage.removeItem('auth_timestamp');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login: loginWithGoogle, // Keep backward compatibility
    loginWithGoogle,
    loginWithEmail,
    register,
    logout,
    processSession,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
